import React, { useMemo } from 'react';
import styled from 'styled-components';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import Text from 'components/atoms/Text';
import { COLOR_SYSTEM } from 'components/design/design-system';
import { AIReviewStatisticsItem, StatisticsUnit } from 'services/api/adminStatistics';

interface ChartProps {
  statistics: AIReviewStatisticsItem[];
  unit: StatisticsUnit;
  loading?: boolean;
}

const StatisticsChart: React.FC<ChartProps> = ({
  statistics,
  unit,
  loading,
}) => {
  // 데이터에서 그룹핑 필드 존재 여부 확인하여 차트 타입 결정
  // status, promptType, userId 중 하나라도 있으면 Bar Chart, 모두 없으면 Line Chart
  const hasGrouping = useMemo(() => {
    return statistics.some((item) =>
      (item.status !== undefined && item.status !== null) ||
      (item.promptType !== undefined && item.promptType !== null && item.promptType !== '') ||
      (item.userId !== undefined && item.userId !== null && item.userId !== '')
    );
  }, [statistics]);
  // 데이터를 Recharts 형식으로 변환
  const chartData = useMemo(() => {
    if (!statistics || statistics.length === 0) return [];

    // 기간별로 그룹화
    const periodMap = new Map<string, any>();

    statistics.forEach((item) => {
      const key = item.periodStartDate;
      if (!periodMap.has(key)) {
        periodMap.set(key, {
          period: formatPeriodLabel(item.periodStartDate, unit),
          periodStartDate: item.periodStartDate,
        });
      }

      // 복합 키 생성: premiumRole-userId-promptType-status 순서
      let dataKey = item.premiumRole;

      // userId가 있으면 추가
      if (item.userId) {
        dataKey += `-${item.userId}`;
      }

      // promptType이 있으면 추가
      if (item.promptType) {
        dataKey += `-${item.promptType}`;
      }

      // status가 있으면 추가
      if (item.status !== undefined) {
        const statusLabel = getStatusLabel(item.status);
        dataKey += `-${statusLabel}`;
      }

      periodMap.get(key)[dataKey] = item.count;
    });

    return Array.from(periodMap.values()).sort(
      (a, b) =>
        new Date(a.periodStartDate).getTime() -
        new Date(b.periodStartDate).getTime()
    );
  }, [statistics, unit]);

  // 범주 목록 추출 (복합 키: premiumRole-userId-promptType-status 순서)
  const categories = useMemo(() => {
    const categorySet = new Set<string>();
    statistics.forEach((item) => {
      let dataKey = item.premiumRole;

      if (item.userId) {
        dataKey += `-${item.userId}`;
      }

      if (item.promptType) {
        dataKey += `-${item.promptType}`;
      }

      if (item.status !== undefined) {
        const statusLabel = getStatusLabel(item.status);
        dataKey += `-${statusLabel}`;
      }

      categorySet.add(dataKey);
    });
    return Array.from(categorySet);
  }, [statistics]);

  // 색상 팔레트 (COLOR_SYSTEM에서 제공하는 색상만 사용)
  const colors = [
    COLOR_SYSTEM.get('Blue')?.[600],
    COLOR_SYSTEM.get('Green')?.[600],
    COLOR_SYSTEM.get('Orange')?.[600],
    COLOR_SYSTEM.get('Purple')?.[600],
    COLOR_SYSTEM.get('Red')?.[600],
    COLOR_SYSTEM.get('Skyblue')?.[600],
    COLOR_SYSTEM.get('Yellow')?.[600],
    COLOR_SYSTEM.get('Brown')?.[600],
  ];

  if (loading) {
    return (
      <LoadingContainer>
        <Text textType="Body1" color={COLOR_SYSTEM.get('Gray')?.[500]}>
          통계 데이터를 불러오는 중입니다...
        </Text>
      </LoadingContainer>
    );
  }

  if (chartData.length === 0) {
    return (
      <EmptyContainer>
        <Text textType="Body1" color={COLOR_SYSTEM.get('Gray')?.[500]}>
          표시할 데이터가 없습니다.
        </Text>
      </EmptyContainer>
    );
  }

  return (
    <ChartContainer>
      <ChartHeader>
        <Text textType="H3" color={COLOR_SYSTEM.get('Gray')?.[900]}>
          Premium Role별 AI Review 통계
        </Text>
      </ChartHeader>

      <ResponsiveContainer width="100%" height={400}>
        {hasGrouping ? (
          <BarChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 60 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="period"
              tick={{ fontSize: 12 }}
              angle={-45}
              textAnchor="end"
              height={80}
            />
            <YAxis
              tick={{ fontSize: 12 }}
              label={{ value: '건수', angle: -90, position: 'insideLeft' }}
            />
            <Tooltip />
            <Legend
              wrapperStyle={{ paddingTop: '20px' }}
              iconType="rect"
            />

            {categories.map((category, index) => (
              <Bar
                key={category}
                dataKey={category}
                fill={colors[index % colors.length]}
              />
            ))}
          </BarChart>
        ) : (
          <LineChart data={chartData} margin={{ top: 5, right: 30, left: 20, bottom: 60 }}>
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis
              dataKey="period"
              tick={{ fontSize: 12 }}
              angle={-45}
              textAnchor="end"
              height={80}
            />
            <YAxis
              tick={{ fontSize: 12 }}
              label={{ value: '건수', angle: -90, position: 'insideLeft' }}
            />
            <Tooltip />
            <Legend wrapperStyle={{ paddingTop: '20px' }} iconType="line" />

            {categories.map((category, index) => (
              <Line
                key={category}
                type="monotone"
                dataKey={category}
                stroke={colors[index % colors.length]}
                strokeWidth={2}
                dot={{ r: 4 }}
                activeDot={{ r: 6 }}
              />
            ))}
          </LineChart>
        )}
      </ResponsiveContainer>
    </ChartContainer>
  );
};

// status 값을 한글 레이블로 변환
function getStatusLabel(status: number): string {
  if (status === -1) return '실패';
  if (status === 400) return '성공';
  return `상태${status}`;
}

// 기간 레이블 포맷팅
function formatPeriodLabel(date: string, unit: string): string {
  if (unit === 'all') {
    return '전체';
  }
  const d = new Date(date);
  if (unit === 'day') {
    return `${d.getMonth() + 1}/${d.getDate()}`;
  } else if (unit === 'week') {
    return `${d.getMonth() + 1}/${d.getDate()}`;
  } else {
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
  }
}

const ChartContainer = styled.div`
  padding: 24px;
  background-color: ${COLOR_SYSTEM.get('Gray')?.[0]};
  border: 1px solid ${COLOR_SYSTEM.get('Gray')?.[200]};
  border-radius: 8px;
`;

const ChartHeader = styled.div`
  margin-bottom: 24px;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 400px;
  border: 1px solid ${COLOR_SYSTEM.get('Gray')?.[200]};
  border-radius: 8px;
  background-color: ${COLOR_SYSTEM.get('Gray')?.[50]};
`;

const EmptyContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 400px;
  border: 1px dashed ${COLOR_SYSTEM.get('Gray')?.[300]};
  border-radius: 8px;
  background-color: ${COLOR_SYSTEM.get('Gray')?.[50]};
`;

export default StatisticsChart;
